import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { RefreshCw } from "lucide-react";
import { formatSectorLabel } from "@/lib/sector-labels";
import { formatRelativeTime, formatMonth, COLORS, STATUS_COLORS } from "./dashboard/utils";
export { COLORS, STATUS_COLORS };
import { StatCards } from "./dashboard/StatCards";
import { ChartSection } from "./dashboard/ChartSection";
import { RecentActivity } from "./dashboard/RecentActivity";

export interface AdminStats {
  totalUsers: number;
  totalWorkspaces: number;
  disabledWorkspaces: number;
  totalAssessments: number;
  totalInvitations: number;
  pendingInvitations: number;
  activeUsers: number;
  inactiveUsers: number;
  assessmentsByStatus: { status: string; count: number }[];
  assessmentsBySector: { sector: string; count: number }[];
  monthlyAssessments: {
    month: string;
    total: number;
    draft: number;
    complete: number;
    finalized: number;
  }[];
  monthlySignups: { month: string; count: number }[];
  avgCompleteness: { status: string; avgCompleteness: number; count: number }[];
  recentUsers: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
    companyName: string | null;
    role: string;
    createdAt: string;
  }[];
  recentAssessments: {
    id: string;
    organizationName: string;
    status: string;
    completenessPercent: number;
    updatedAt: string;
    workspaceName: string | null;
  }[];
}

async function fetchStats(): Promise<AdminStats> {
  const response = await fetch("/api/admin/stats", { credentials: "include" });
  if (!response.ok) throw new Error("Failed to fetch stats");
  return response.json();
}

export default function AdminDashboard() {
  const { user } = useAuth();

  const { data: stats, isLoading, isError, refetch, isFetching, dataUpdatedAt } = useQuery({
    queryKey: ["/api/admin/stats"],
    queryFn: fetchStats,
    enabled: user?.role === "admin",
  });

  const monthlyChartData = useMemo(
    () => (stats?.monthlyAssessments ?? []).map((m) => ({ ...m, month: formatMonth(m.month) })),
    [stats?.monthlyAssessments]
  );

  const signupChartData = useMemo(
    () => (stats?.monthlySignups ?? []).map((m) => ({ ...m, month: formatMonth(m.month) })),
    [stats?.monthlySignups]
  );

  const statusPieData = useMemo(
    () => (stats?.assessmentsByStatus ?? []).map((s) => ({ name: s.status, value: s.count })),
    [stats?.assessmentsByStatus]
  );

  const sectorBarData = useMemo(
    () => (stats?.assessmentsBySector ?? []).map((s) => ({ name: formatSectorLabel(s.sector), value: s.count })),
    [stats?.assessmentsBySector]
  );

  const derivedStats = useMemo(() => {
    if (!stats) return { overallAvgCompleteness: 0, mostActiveSector: null, lastActivityAt: null };
    const totalWeighted = stats.avgCompleteness.reduce((s, i) => s + i.avgCompleteness * i.count, 0);
    const totalCount = stats.avgCompleteness.reduce((s, i) => s + i.count, 0);
    const overallAvgCompleteness = totalCount > 0 ? Math.round(totalWeighted / totalCount) : 0;
    const mostActiveSector =
      stats.assessmentsBySector.length > 0
        ? stats.assessmentsBySector.reduce((a, b) => (a.count >= b.count ? a : b))
        : null;
    const lastActivityAt = stats.recentAssessments[0]?.updatedAt ?? null;
    return { overallAvgCompleteness, mostActiveSector, lastActivityAt };
  }, [stats?.avgCompleteness, stats?.assessmentsBySector, stats?.recentAssessments]);

  const assessmentTrendNote = useMemo(() => {
    const months = stats?.monthlyAssessments ?? [];
    if (months.length < 2) return null;
    const latest = months[months.length - 1].total;
    const prev = months[months.length - 2].total;
    const diff = latest - prev;
    if (diff === 0) return null;
    return { count: Math.abs(diff), direction: diff > 0 ? "up" : "down" } as const;
  }, [stats?.monthlyAssessments]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (isError || !stats) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-3">
          <p className="text-slate-600">{"Could not load dashboard data"}</p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 text-sm font-medium text-primary border border-primary/30 rounded-xl hover:bg-primary/5 transition-colors"
          >
            {"Try again"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-4xl font-extrabold tracking-tight text-slate-900">
            {"Admin Dashboard"}
          </h1>
          <p className="text-slate-500 mt-2">{"Platform overview and key metrics"}</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-400 mt-1">
          <span>{`Updated ${formatRelativeTime(dataUpdatedAt)}`}</span>
          <button
            onClick={() => refetch()}
            className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors text-slate-500"
            aria-label={"Refresh"}
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      <StatCards stats={stats} derivedStats={derivedStats} />

      <ChartSection
        monthlyChartData={monthlyChartData}
        signupChartData={signupChartData}
        statusPieData={statusPieData}
        sectorBarData={sectorBarData}
        avgCompleteness={stats.avgCompleteness}
        assessmentTrendNote={assessmentTrendNote}
      />

      <RecentActivity
        recentUsers={stats.recentUsers}
        recentAssessments={stats.recentAssessments}
      />
    </div>
  );
}
